import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AlignLeft, AlignCenter, AlignRight, AlignJustify, Link as AttachLink,
    Baseline, Bold, ChevronDown, Highlighter, Italic, List, ListOrdered, 
    Redo2, Undo2, Underline as UnderlineText 
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor, JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import Link from '@tiptap/extension-link';
import { requiredMark } from './formHelpers';
import './RichTextEditor.scss';

interface RichTextEditorProps {
    label?: string;
    required?: boolean;
    /** Initial content — either an HTML string (legacy) or Tiptap JSON. */
    value?: string | JSONContent;
    placeholder?: string;
    /** When true, focuses the editor (at the start) once it mounts. */
    autoFocus?: boolean;
    /** Fires with the editor's HTML on every change. */
    onChange?: (html: string) => void;
    /** Fires with the editor's ProseMirror JSON on every change. */
    onChangeJson?: (json: JSONContent) => void;
    /**
     * Fires with the editor's plain text on every change (block nodes separated
     * by a single newline). Use this when the caller stores JSON as the canonical
     * rich content and only wants a plain-text mirror in the legacy string field.
     */
    onChangeText?: (text: string) => void;
}

type HeadingLevel = 1 | 2 | 3;

/**
 * Normalize empty / whitespace-only paragraphs in pasted HTML to a single clean
 * empty paragraph (<p></p>).
 *
 * Pasting from Word, Outlook, Google Docs or email commonly brings in blank
 * paragraphs like <p>&nbsp;</p>, <p> </p> or <p><span>&nbsp;</span></p>. Their
 * stray whitespace/markup makes them render as oversized gaps and stops the
 * `p:empty` styling from applying. We strip each one down to <p></p> so it
 * renders as exactly one modest blank line — preserving the author's spacing
 * without the large gap, and without the doubled line a <br> would cause
 * (ProseMirror turns a between-paragraph <br> into an extra trailing hard
 * break). Paragraphs with real content (text, or embedded media such as images)
 * are left untouched, and blank lines a user creates by pressing Enter in the
 * editor are unaffected — this only runs on pasted content.
 */
export function normalizeEmptyParagraphs(html: string): string {
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return html;
    }
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('p').forEach((p) => {
        // Normalize non-breaking spaces to regular spaces before checking for text.
        const text = (p.textContent || '').replace(/\u00a0/g, ' ').trim();
        const hasMedia = p.querySelector('img, iframe, video, audio');
        if (text === '' && !hasMedia) {
            // Clear stray whitespace/markup, leaving a clean empty paragraph that
            // renders as a single blank line.
            p.innerHTML = '';
        }
    });
    return doc.body.innerHTML;
}

/**
 * Unwrap <p> elements nested inside list items.
 *
 * Word / Google Docs paste wraps each bullet's text in a paragraph, e.g.
 * <li><p>Item</p></li>. That paragraph adds its own vertical margins inside the
 * bullet, producing uneven, oversized list spacing. We lift the paragraph's
 * content directly into the <li> (<li>Item</li>). When a single item holds
 * multiple paragraphs, they're separated with a <br> so their line breaks
 * survive. Only paragraphs that are direct children of an <li> are touched, so
 * nested lists are handled correctly and block-level paragraphs are left alone.
 */
export function unwrapListItemParagraphs(html: string): string {
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return html;
    }
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('li').forEach((li) => {
        const paragraphs = Array.from(li.children).filter((child) => child.tagName === 'P');
        paragraphs.forEach((p, index) => {
            // Keep the line break between stacked paragraphs in the same item.
            if (index > 0) {
                li.insertBefore(doc.createElement('br'), p);
            }
            while (p.firstChild) {
                li.insertBefore(p.firstChild, p);
            }
            li.removeChild(p);
        });
    });
    return doc.body.innerHTML;
}

function RichTextEditor({ label, required = false, value = '', placeholder = 'Write something...', autoFocus = false, onChange, onChangeJson, onChangeText }: RichTextEditorProps) {
    const [headingOpen, setHeadingOpen] = useState(false);
    const [listOpen, setListOpen] = useState(false);
    const headingRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const editor = useEditor({
        extensions: [
            // Tiptap v3 StarterKit bundles Underline, Link and ListKeymap.
            // We register our own configured Link below, so disable the bundled
            // one here to avoid a duplicate-extension error. Underline is used
            // as-is from StarterKit.
            StarterKit.configure({ link: false }),
            Highlight,
            TextStyle,
            Color,
            Placeholder.configure({
                placeholder,
            }),
            Link.configure({
                openOnClick: true,
                autolink: true, 
                linkOnPaste: true,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        editorProps: {
            // Clean pasted content (Word/Outlook/Docs/email): collapse empty
            // paragraphs to a single blank line, then unwrap <p> nested inside
            // list items so bullets don't get oversized, uneven spacing.
            transformPastedHTML: (html) => unwrapListItemParagraphs(normalizeEmptyParagraphs(html)),
        },
        // v3 no longer re-renders the React component on every transaction by
        // default; our toolbar relies on editor.isActive()/can() reflecting the
        // current selection, so opt back in to keep the buttons in sync.
        shouldRerenderOnTransaction: true,
        // Focus the first field when the form opens (replaces the old
        // ref-based autofocus that no longer worked once fields became editors).
        autofocus: autoFocus ? 'start' : false,
        content: value,
        onUpdate: ({ editor: e }: { editor: Editor }) => {
            // Emit each requested format so callers can persist HTML (legacy/preview),
            // JSON (canonical), and/or plain text in parallel.
            onChange?.(e.getHTML());
            onChangeJson?.(e.getJSON());
            onChangeText?.(e.getText({ blockSeparator: '\n' }));
        },
    });

    const closeDropdowns = useCallback((e: MouseEvent) => {
        if (headingRef.current && !headingRef.current.contains(e.target as Node)) {
            setHeadingOpen(false);
        }
        if (listRef.current && !listRef.current.contains(e.target as Node)) {
            setListOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', closeDropdowns);
        return () => document.removeEventListener('mousedown', closeDropdowns);
    }, [closeDropdowns]);

    if (!editor) return null;

    const getHeadingLabel = () => {
        for (const level of [1, 2, 3] as HeadingLevel[]) {
            if (editor.isActive('heading', { level })) return `H${level}`;
        }
        return 'H';
    };

    const getListLabel = () => {
        // if (editor.isActive('bulletList')) return '• Lists';
        // if (editor.isActive('orderedList')) return '1. List';
        return (
            <span className="rte-icon">
                <List size={20} />
                <ChevronDown size={16} />
            </span>)
    };

    const handleLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl || 'https://');
        if (url === null) return; // cancelled
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    };

    return (
        <div className="rich-text-editor" data-testid="rich-text-editor">
            <span className="rich-text-label">{label}{required && requiredMark()}</span>

            <div className="rte-toolbar" role="toolbar" aria-label="Text formatting">
                {/* Undo / Redo */}
                <div className="rte-toolbar__group">
                    <button type="button" title="Undo"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}>
                        <span className="rte-icon"><Undo2 size={16} /></span>
                    </button>
                    <button type="button" title="Redo"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}>
                        <span className="rte-icon"><Redo2 size={16} /></span>
                    </button>
                </div>

                <span className="rte-toolbar__divider" />

                {/* Heading dropdown */}
                <div className="rte-toolbar__group rte-dropdown" ref={headingRef}>
                    <button type="button" title="Heading"
                        className={editor.isActive('heading') ? 'active' : ''}
                        onClick={() => { setHeadingOpen(prev => !prev); setListOpen(false); }}
                    >{getHeadingLabel()} ▾</button>
                    {headingOpen && (
                        <div className="rte-dropdown__menu">
                            {([1, 2, 3] as HeadingLevel[]).map(level => (
                                <button key={level} type="button"
                                    className={editor.isActive('heading', { level }) ? 'active' : ''}
                                    onClick={() => { editor.chain().focus().toggleHeading({ level }).run(); setHeadingOpen(false); }}
                                >Heading {level}</button>
                            ))}
                            <button type="button"
                                className={!editor.isActive('heading') ? 'active' : ''}
                                onClick={() => { editor.chain().focus().setParagraph().run(); setHeadingOpen(false); }}
                            >Normal text</button>
                        </div>
                    )}
                </div>

                <span className="rte-toolbar__divider" />

                {/* Text formatting */}
                <div className="rte-toolbar__group">
                    <button type="button" title="Bold"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={editor.isActive('bold') ? 'active' : ''}>
                        <span className="rte-icon"><Bold size={16} strokeWidth={3} /></span>
                    </button>
                    <button type="button" title="Italic"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={editor.isActive('italic') ? 'active' : ''}>
                        <span className="rte-icon"><Italic size={16} /></span>
                        </button>
                    <button type="button" title="Underline"
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        className={editor.isActive('underline') ? 'active' : ''}>
                        <span className="rte-icon"><UnderlineText size={16} /></span>
                    </button>
                    <button type="button" title="Highlight"
                        onClick={() => editor.chain().focus().toggleHighlight().run()}
                        className={editor.isActive('highlight') ? 'active' : ''}>
                        <span className="rte-icon"><Highlighter size={16} /></span>
                    </button>
                    <button className="rte-color-btn" title="Font Color">
                        <span className="rte-icon"><Baseline size={18} color="red" /></span>
                        <input type="color" className="rte-color-input"
                            value={editor.getAttributes('textStyle').color || '#000000'}
                            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                        />
                    </button>
                    <button type="button" title="Link"
                        onClick={handleLink}
                        className={editor.isActive('link') ? 'active' : ''}
                    >
                        <span className="rte-icon"><AttachLink size={16} /></span>
                    </button>
                </div>

                <span className="rte-toolbar__divider" />

                {/* List dropdown */}
                <div className="rte-toolbar__group rte-dropdown" ref={listRef}>
                    <button type="button" title="List"
                        className={editor.isActive('bulletList') || editor.isActive('orderedList') ? 'active' : ''}
                        onClick={() => { setListOpen(prev => !prev); setHeadingOpen(false); }}
                    >{getListLabel()}</button>
                    {listOpen && (
                        <div className="rte-dropdown__menu">
                            <button type="button"
                                className={editor.isActive('bulletList') ? 'active' : ''}
                                onClick={() => { editor.chain().focus().toggleBulletList().run(); setListOpen(false); }}
                            >
                                <span className="rte-icon"><List size={18} /></span>
                                <span className="rte-icon-spacing">Bullet List</span>
                            </button>
                            <button type="button"
                                className={editor.isActive('orderedList') ? 'active' : ''}
                                onClick={() => { editor.chain().focus().toggleOrderedList().run(); setListOpen(false); }}
                            >
                                <span className="rte-icon"><ListOrdered size={18} /></span>
                                <span className="rte-icon-spacing">Ordered List</span>
                            </button>
                        </div>
                    )}
                </div>

                <span className="rte-toolbar__divider" />

                {/* Alignment */}
                <div className="rte-toolbar__group">
                    <button type="button" title="Align Left"
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''}>
                        <span className="rte-icon">
                            <AlignLeft size={20} />
                        </span>
                    </button>
                    <button type="button" title="Align Center"
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''}>
                        <span className="rte-icon">
                            <AlignCenter size={20} />
                        </span>        
                    </button>
                    <button type="button" title="Align Right"
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''}>
                        <span className="rte-icon">
                            <AlignRight size={20} />
                        </span>  
                    </button>
                    <button type="button" title="Align Justify"
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                        className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}>
                        <span className="rte-icon">
                            <AlignJustify size={20} />
                        </span>  
                    </button>
                </div>
            </div>

            <div className="rte-content">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}

export default RichTextEditor;
